import React, { useRef } from 'react';
import { OrderAuditReport } from '../../types';
import { OrderAuditPrintView } from './order-audit-print-view';
import { Button } from '../ui/button';
import { useModalA11y } from '../../hooks/use-modal-a11y';

interface OrderAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditReport: OrderAuditReport | null;
  isLoading?: boolean;
}

/**
 * Order Audit Modal Component
 * 
 * Modal dialog that displays the order audit report with print functionality.
 * Uses the browser's native print API.
 */
export function OrderAuditModal({ isOpen, onClose, auditReport, isLoading }: OrderAuditModalProps) {
  const handlePrint = () => {
    window.print();
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 print:hidden"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-50 overflow-auto flex items-center justify-center p-4 print:relative print:block print:p-0">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-audit-modal-title"
          tabIndex={-1}
          className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col print:max-w-none print:max-h-none print:shadow-none print:rounded-none focus:outline-none"
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-secondary-700 print:hidden">
            <h2 id="order-audit-modal-title" className="text-xl font-semibold text-gray-900 dark:text-white">
              Order Audit Report
            </h2>
            <div className="flex items-center gap-2">
              {auditReport && (
                <Button
                  onClick={handlePrint}
                  variant="primary"
                  disabled={isLoading}
                >
                  <svg 
                    className="w-4 h-4 mr-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" 
                    />
                  </svg>
                  Print Report
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 transition-colors"
                aria-label="Close modal"
              >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-secondary-900 print:p-0 print:bg-white dark:bg-secondary-800 print:overflow-visible">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-secondary-400">Loading audit report...</p>
                </div>
              </div>
            )}

            {!isLoading && !auditReport && (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500 dark:text-secondary-400">No audit report available</p>
              </div>
            )}

            {!isLoading && auditReport && (
              <OrderAuditPrintView auditReport={auditReport} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
